// src/services/PricingEngine.js
class PricingEngine {
  constructor() {
    // Legacy mock data - now using calendar allowances instead
    this.mockHostelPrices = {
      'tirana': { base: 18, weekend: 22, peak: 28 },
      'shkoder': { base: 15, weekend: 18, peak: 22 },
      'valbona': { base: 20, weekend: 25, peak: 35 },
      'theth': { base: 22, weekend: 28, peak: 38 },
      'ksamil': { base: 25, weekend: 32, peak: 45 },
      'saranda': { base: 22, weekend: 28, peak: 40 },
      'berat': { base: 16, weekend: 20, peak: 25 },
      'gjirokaster': { base: 18, weekend: 22, peak: 28 }
    };

    // Legacy peak season logic - now using calendar data
    this.peakDates = {
      summer: { start: '06-15', end: '09-15' },
      christmas: { start: '12-20', end: '01-05' },
      easter: { start: '04-10', end: '04-20' }
    };
  }

  // Calculate expected daily costs for a date range using calendar allowances
  async getExpectedCostsForDateRange(route, startDate, endDate) {
    try {
      
      // Import services dynamically to avoid circular dependencies
      const { default: allotmentService } = await import('./allotmentService');
      const { default: calendarVersionService } = await import('./calendarVersionService');
      
      // Try to get allowances from default version first, then current calendar
      let allowances = [];
      let dataSource = 'none';
      
      // Check if there's a default version for pricing
      const defaultVersion = await calendarVersionService.getDefaultVersion(route.id);
      if (defaultVersion) {
        console.log('📊 Using default version for pricing:', defaultVersion.versionName);
        const versionData = await calendarVersionService.getCalendarVersion(defaultVersion.id);
        allowances = versionData.allowances;
        dataSource = 'default-version';
      } else {
        console.log('📅 No default version found, using current calendar data');
        // Fallback to current calendar allowances
        allowances = await allotmentService.getAllowancesForDateRange(route.id, startDate, endDate);
        dataSource = 'current-calendar';
      }
      
      console.log(`💵 Found ${allowances.length} allowance records from ${dataSource}:`, allowances);
      
      const dailyCosts = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // For each day in the range (excluding checkout day)
      for (let date = new Date(start); date < end; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0];
        
        // Find allowance for this specific date
        const dayAllowance = allowances.find(a => a.date === dateStr);
        const dailyAllowance = dayAllowance?.dailyAllowance || 0;
        
        // Use allowance as expected cost (what BackPass covers)
        // Add small buffer for pricing calculation
        const expectedCost = dailyAllowance * 1.15; // 15% buffer above allowance
        
        dailyCosts.push({
          date: dateStr,
          cost: Math.max(25, Math.round(expectedCost)), // Minimum $25
          isWeekend: date.getDay() === 5 || date.getDay() === 6,
          isPeak: this.isPeakSeason(date),
          dailyAllowance: dailyAllowance,
          hasAllowanceData: !!dayAllowance
        });
      }
      
      return dailyCosts;
      
    } catch (error) {
      console.error('Error getting allowance-based costs:', error);
      
      // Fallback to legacy mock calculation
      return this.getLegacyExpectedCosts(startDate, endDate);
    }
  }

  // Legacy fallback method
  getLegacyExpectedCosts(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dailyCosts = [];
    
    for (let date = new Date(start); date < end; date.setDate(date.getDate() + 1)) {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 5 || dayOfWeek === 6;
      const isPeak = this.isPeakSeason(date);
      
      let totalCost = 0;
      let cityCount = 0;
      
      Object.values(this.mockHostelPrices).forEach(cityPrices => {
        if (isWeekend) {
          totalCost += cityPrices.weekend;
        } else {
          totalCost += cityPrices.base;
        }
        cityCount++;
      });
      
      dailyCosts.push({
        date: date.toISOString().split('T')[0],
        cost: Math.round(totalCost / cityCount),
        isWeekend,
        isPeak,
        isLegacyFallback: true
      });
    }
    
    return dailyCosts;
  }

  // Check if date is in peak season
  isPeakSeason(date) {
    const monthDay = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    // Fix: Remove unused 'season' variable from destructuring
    for (const range of Object.values(this.peakDates)) {
      if (monthDay >= range.start && monthDay <= range.end) {
        return true;
      }
    }
    
    // Handle year-end overlap
    if (monthDay >= '12-20' || monthDay <= '01-05') {
      return true;
    }
    
    return false;
  }

  // Calculate lead time multiplier
  getLeadTimeMultiplier(startDate, pricingSettings) {
    const daysUntilStart = Math.ceil((new Date(startDate) - new Date()) / (1000 * 60 * 60 * 24));
    const multipliers = pricingSettings?.leadTimeMultipliers || {
      sameWeek: 1.15,
      nextWeek: 1.10,
      nextMonth: 1.00,
      twoMonthsPlus: 0.95
    };
    
    if (daysUntilStart <= 7) return multipliers.sameWeek;
    if (daysUntilStart <= 14) return multipliers.nextWeek;
    if (daysUntilStart <= 30) return multipliers.nextMonth;
    if (daysUntilStart >= 60) return multipliers.twoMonthsPlus;
    return 1.00;
  }

  // Main pricing calculation based on calendar allowances
  async calculatePassPrice(route, startDate, endDate, pricingSettings = {}) {
    try {
      // Get daily allowances from calendar (or default version)
      const expectedDailyCosts = await this.getExpectedCostsForDateRange(route, startDate, endDate);
      
      // Check if we have real allowance data vs fallback
      const hasAllowanceData = expectedDailyCosts.some(day => day.hasAllowanceData);
      
      // Sum up total daily allowances (what BackPass actually covers)
      const totalDailyAllowances = expectedDailyCosts.reduce((sum, day) => sum + (day.dailyAllowance || 0), 0);
      
      // Get route-specific profit margin (default to $50 if not set)
      const routeProfitMargin = route.profitMargin || 50;
      const isPercentage = route.profitMarginType === 'percentage';
      
      let finalPrice;
      if (isPercentage) {
        // Apply percentage margin to total allowances
        finalPrice = Math.round(totalDailyAllowances * (1 + routeProfitMargin / 100));
      } else {
        // Add flat dollar amount to total allowances
        finalPrice = Math.round(totalDailyAllowances + routeProfitMargin);
      }
      
      // Calculate daily allocations for display
      const dailyAllocations = expectedDailyCosts.map(day => ({
        date: day.date,
        dailyAllowance: day.dailyAllowance || 0,
        isWeekend: day.isWeekend,
        isPeak: day.isPeak,
        hasAllowanceData: day.hasAllowanceData
      }));
      
      
      return {
        price: finalPrice,
        dailyAllocations,
        summary: {
          nights: expectedDailyCosts.length, // Each day in range = 1 night stayed
          totalDailyAllowances: Math.round(totalDailyAllowances),
          avgDailyAllowance: Math.round(totalDailyAllowances / expectedDailyCosts.length),
          routeProfitMargin: isPercentage ? `${routeProfitMargin}%` : `$${routeProfitMargin}`,
          profitMarginType: route.profitMarginType || 'dollar',
          usingCalendarData: hasAllowanceData,
          fallbackUsed: !hasAllowanceData
        }
      };
    } catch (error) {
      console.error('Pricing calculation error:', error);
      // Fallback to simple calculation
      const nights = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
      const basePrice = (route.storePrice || 599) / 5 * nights;
      return {
        price: Math.round(basePrice),
        dailyAllocations: [],
        summary: { 
          nights, 
          fallback: true, 
          error: error.message,
          usingCalendarData: false
        }
      };
    }
  }
}

// Fix: Create named instance before exporting
const pricingEngine = new PricingEngine();
export default pricingEngine;